import { isPlatformBrowser } from '@angular/common';
import { Injectable, PLATFORM_ID, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of, Subscription } from 'rxjs';
import { catchError, finalize, map, switchMap, take } from 'rxjs/operators';
import { CDESyncCouplings } from '@cde-fe-app/app/shared/models/cde-sync-couplings.model';
import { CouplingsService } from '@cde-fe-app/app/shared/services/couplings.service';
import { MachinesService } from '@cde-fe-app/app/shared/services/machines.service';
import * as RootState from '..';
import * as CouplingsActions from './couplings.actions';

@Injectable()
export class CouplingsEffects {
  readonly store = inject<Store<RootState.IState>>(Store<RootState.IState>);
  readonly actions$ = inject(Actions);
  readonly couplingsService = inject(CouplingsService);
  readonly machinesService = inject(MachinesService);
  readonly platformId = inject<number | string>(PLATFORM_ID);

  list$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouplingsActions.COUPLINGS),
      map((action: CouplingsActions.CouplingsList) => action.payload),
      switchMap(timeZone =>
        this.couplingsService.getCouplings(timeZone).pipe(
          take(1),
          map((info: CDESyncCouplings) => {
            const couplings = Object.values(info);
            return new CouplingsActions.CouplingsSuccess(couplings);
          }),
          catchError(error => of(new CouplingsActions.CouplingsFailure(error))),
          finalize(() => {
            this.store.dispatch(new CouplingsActions.CouplingsComplete());
          })
        )
      )
    )
  );

  post$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouplingsActions.POST_COUPLINGS),
      map((action: CouplingsActions.PostCouplings) => action.payload),
      switchMap(partnerId =>
        this.couplingsService.postCouplings(partnerId).pipe(
          take(1),
          map((info: CDESyncCouplings) => {
            if (info && info.acceptorUrl) {
              if (isPlatformBrowser(this.platformId)) {
                // Open 365 app in the current tab
                window.location.href = info.acceptorUrl;
              }
            }
            return new CouplingsActions.PostCouplingsSuccess();
          }),
          catchError(error => of(new CouplingsActions.PostCouplingsFailure(error))),
          finalize(() => {
            this.store.dispatch(new CouplingsActions.PostCouplingsComplete());
          })
        )
      )
    )
  );
  delete$ = createEffect(() =>
    this.actions$.pipe(
      ofType(CouplingsActions.DELETE_COUPLINGS),
      map((action: CouplingsActions.DeleteCouplings) => action.payload),
      switchMap((couplingId: string) =>
        this.couplingsService.deleteCoupling(couplingId).pipe(
          take(1),
          map(() => new CouplingsActions.DeleteCouplingsSuccess()),
          catchError(error => of(new CouplingsActions.DeleteCouplingsFailure(error))),
          finalize(() => {
            // TODO: This watcher has to be unsubscriped
            this.timeZoneWatcher = this.machinesService.unit$.subscribe((zone: string) => {
              this.store.dispatch(new CouplingsActions.CouplingsList(zone));
            });
            this.store.dispatch(new CouplingsActions.DeleteCouplingsComplete());
          })
        )
      )
    )
  );

  private timeZoneWatcher: Subscription;
}
